import semverGt from 'semver/functions/gt'
import semverLt from 'semver/functions/lt'
import semverValid from 'semver/functions/valid'
import { z } from 'zod'
import { Environment } from '~/types/EnvironmentTypes'
import { AccountUserLevel } from '~/utils/auth'
import { PartnerId, Partners } from '~/utils/partner'

// The app CSP blocks eval, and zod's JIT probe (`new Function`) reports a
// violation even when it is caught.
z.config({ jitless: true })

export const ANNOUNCEMENT_SEVERITIES = ['info', 'warning', 'critical'] as const
export type AnnouncementSeverity = (typeof ANNOUNCEMENT_SEVERITIES)[number]

export type AnnouncementDismissMode = 'none' | 'collapse' | 'dismiss'
export type AnnouncementPlatform = 'web' | 'desktop'

const ACCOUNT_USER_LEVELS = [
  'limited',
  'restricted',
  'unrestricted',
  'admin',
  'senior',
  'super',
] as const satisfies readonly AccountUserLevel[]

/** `default` is a user with no partner (Mango branding). */
export const DEFAULT_PARTNER_AUDIENCE = 'default'

const PARTNER_AUDIENCE_VALUES = [
  DEFAULT_PARTNER_AUDIENCE,
  ...Object.values(Partners).filter((partner) => partner !== Partners.Default),
] as [string, ...string[]]

const ALLOWED_LINK_HOST_SUFFIXES = ['mangovoice.com', 'mangovoice.co.uk']
const ALLOWED_LINK_HOSTS = ['mangovoice.my.site.com']

const IN_APP_ORIGIN = 'https://in-app.invalid'

// The URL parser drops tab and newline, so "/\t/evil.com" resolves off-site;
// resolve the path and require the same origin.
export const isInAppPath = (href: string): boolean => {
  if (!href.startsWith('/') || /[\s\\]/.test(href)) return false
  try {
    return new URL(href, IN_APP_ORIGIN).origin === IN_APP_ORIGIN
  } catch {
    return false
  }
}

/** True for in-app paths and https links to Mango-owned hosts. */
export const isAllowedAnnouncementLink = (href: string): boolean => {
  if (isInAppPath(href)) return true

  let url: URL
  try {
    url = new URL(href)
  } catch {
    return false
  }
  if (url.protocol !== 'https:' || url.username || url.password) return false

  const host = url.hostname.toLowerCase()
  return (
    ALLOWED_LINK_HOSTS.includes(host) ||
    ALLOWED_LINK_HOST_SUFFIXES.some(
      (suffix) => host === suffix || host.endsWith(`.${suffix}`),
    )
  )
}

const normalizeLocale = (locale: string) =>
  locale.trim().toLowerCase().replace(/_/g, '-')

const localizedTextSchema = z
  .record(z.string(), z.string().trim().min(1))
  .transform((text) =>
    Object.fromEntries(
      Object.entries(text).map(([locale, value]) => [
        normalizeLocale(locale),
        value,
      ]),
    ),
  )
  .refine((text) => typeof text.en === 'string', {
    message: 'English (en) text is required',
  })

export type LocalizedText = z.infer<typeof localizedTextSchema>

const dateTimeSchema = z.iso
  .datetime({ offset: true })
  .transform((value) => new Date(value))

const semverSchema = z.string().refine((value) => !!semverValid(value), {
  message: 'Must be a semver version, for example 1.0.95',
})

const normalizeCountry = (country: string) => country.trim().toUpperCase()

const countrySchema = z
  .string()
  .trim()
  .regex(/^[a-z]{2}$/i, 'Use a two-letter ISO country code, for example US')
  .transform(normalizeCountry)
  .refine((country) => country !== 'UK', {
    message: 'Use GB for the United Kingdom',
  })

const audienceSchema = z.strictObject({
  roles: z.array(z.enum(ACCOUNT_USER_LEVELS)).min(1).optional(),
  regions: z.array(z.enum(Environment)).min(1).optional(),
  platforms: z
    .array(z.enum(['web', 'desktop']))
    .min(1)
    .optional(),
  partners: z.array(z.enum(PARTNER_AUDIENCE_VALUES)).min(1).optional(),
  countries: z.array(countrySchema).min(1).optional(),
  minVersion: semverSchema.optional(),
  maxVersion: semverSchema.optional(),
})

const ctaSchema = z.strictObject({
  label: localizedTextSchema,
  url: z.string().refine(isAllowedAnnouncementLink, {
    message: 'Link must be an in-app path or an approved Mango domain',
  }),
})

const announcementSchema = z
  .strictObject({
    id: z
      .string()
      .regex(/^[a-z0-9][a-z0-9_-]*$/i, 'Use letters, digits, "-" and "_"'),
    version: z.number().int().positive(),
    severity: z.enum(ANNOUNCEMENT_SEVERITIES),
    title: localizedTextSchema,
    body: localizedTextSchema.optional(),
    startsAt: dateTimeSchema.optional(),
    endsAt: dateTimeSchema.optional(),
    audience: audienceSchema.optional(),
    dismissMode: z.enum(['none', 'collapse', 'dismiss']).optional(),
    cta: ctaSchema.optional(),
  })
  .superRefine((entry, ctx) => {
    if (entry.severity === 'critical' && entry.dismissMode === 'dismiss') {
      ctx.addIssue({
        code: 'custom',
        path: ['dismissMode'],
        message: 'critical announcements cannot use dismiss',
      })
    }
    if (entry.startsAt && entry.endsAt && entry.startsAt >= entry.endsAt) {
      ctx.addIssue({
        code: 'custom',
        path: ['endsAt'],
        message: 'endsAt must be after startsAt',
      })
    }
  })
  .transform(({ dismissMode, ...entry }) => ({
    ...entry,
    dismissMode: (dismissMode ??
      (entry.severity === 'critical'
        ? 'collapse'
        : 'dismiss')) as AnnouncementDismissMode,
  }))

export type Announcement = z.infer<typeof announcementSchema>

const envelopeSchema = z.object({ announcements: z.array(z.unknown()) })

export interface AnnouncementIssue {
  /** Entry position in the payload; -1 when the envelope itself is invalid. */
  index: number
  id?: string
  reason: 'invalid_envelope' | 'invalid_entry' | 'duplicate_id'
  problems: string[]
}

export interface ParsedAnnouncements {
  announcements: Announcement[]
  issues: AnnouncementIssue[]
}

const describeIssues = (error: z.ZodError): string[] =>
  error.issues.map(
    (issue) => `${issue.path.join('.') || '(root)'}: ${issue.message}`,
  )

const readEntryId = (entry: unknown): string | undefined => {
  if (entry && typeof entry === 'object' && 'id' in entry) {
    const { id } = entry as { id: unknown }
    if (typeof id === 'string') return id
  }
  return undefined
}

/**
 * Validates a raw flag payload. Invalid and duplicate entries are dropped and
 * returned as issues; valid siblings are kept.
 */
export const parseAnnouncementPayload = (raw: unknown): ParsedAnnouncements => {
  if (raw === undefined || raw === null) {
    return { announcements: [], issues: [] }
  }

  let payload = raw
  if (typeof raw === 'string') {
    try {
      payload = JSON.parse(raw)
    } catch {
      return {
        announcements: [],
        issues: [
          { index: -1, reason: 'invalid_envelope', problems: ['Invalid JSON'] },
        ],
      }
    }
  }

  const envelope = envelopeSchema.safeParse(payload)
  if (!envelope.success) {
    return {
      announcements: [],
      issues: [
        {
          index: -1,
          reason: 'invalid_envelope',
          problems: describeIssues(envelope.error),
        },
      ],
    }
  }

  const announcements: Announcement[] = []
  const issues: AnnouncementIssue[] = []
  const seenIds = new Set<string>()

  envelope.data.announcements.forEach((entry, index) => {
    const result = announcementSchema.safeParse(entry)
    if (!result.success) {
      issues.push({
        index,
        id: readEntryId(entry),
        reason: 'invalid_entry',
        problems: describeIssues(result.error),
      })
      return
    }
    if (seenIds.has(result.data.id)) {
      issues.push({
        index,
        id: result.data.id,
        reason: 'duplicate_id',
        problems: ['An earlier entry has the same id'],
      })
      return
    }
    seenIds.add(result.data.id)
    announcements.push(result.data)
  })

  return { announcements, issues }
}

/** Picks the user's locale, then its base language, then English. */
export const resolveLocalizedText = (
  text: LocalizedText,
  language: string | undefined,
): string => {
  if (language) {
    const locale = normalizeLocale(language)
    const base = locale.split('-')[0]
    if (text[locale]) return text[locale]
    if (text[base]) return text[base]
  }
  return text.en
}

export interface AnnouncementContext {
  now: Date
  hasRole: (role: AccountUserLevel) => boolean
  region: Environment
  platform: AnnouncementPlatform
  /** The partner the app is branded for; '' when there is none. */
  partner: PartnerId
  /** ISO code of the PBX company's country; null or undefined when unknown. */
  companyCountry: string | null | undefined
  /** Installed desktop version; undefined on web or while it loads. */
  appVersion: string | undefined
}

const isInTimeWindow = (
  { startsAt, endsAt }: Announcement,
  now: Date,
): boolean => (!startsAt || now >= startsAt) && (!endsAt || now < endsAt)

const matchesAudience = (
  { audience }: Announcement,
  context: AnnouncementContext,
): boolean => {
  if (!audience) return true
  const {
    roles,
    regions,
    platforms,
    partners,
    countries,
    minVersion,
    maxVersion,
  } = audience

  if (roles && !roles.some(context.hasRole)) return false
  if (regions && !regions.includes(context.region)) return false
  if (platforms && !platforms.includes(context.platform)) return false
  if (
    partners &&
    !partners.includes(context.partner || DEFAULT_PARTNER_AUDIENCE)
  ) {
    return false
  }
  if (
    countries &&
    !(
      context.companyCountry &&
      countries.includes(normalizeCountry(context.companyCountry))
    )
  ) {
    return false
  }

  if (minVersion || maxVersion) {
    const version = context.appVersion
    if (context.platform !== 'desktop' || !version || !semverValid(version)) {
      return false
    }
    if (minVersion && semverLt(version, minVersion)) return false
    if (maxVersion && semverGt(version, maxVersion)) return false
  }

  return true
}

export const isAnnouncementEligible = (
  announcement: Announcement,
  context: AnnouncementContext,
): boolean =>
  isInTimeWindow(announcement, context.now) &&
  matchesAudience(announcement, context)

/** Newest `startsAt` first; entries without one go last, in payload order. */
export const compareAnnouncements = (a: Announcement, b: Announcement) =>
  (b.startsAt?.getTime() ?? -Infinity) - (a.startsAt?.getTime() ?? -Infinity)

/** Storage key for per-user, per-version state: `userId:id@version`. */
export const getAnnouncementStorageKey = (
  userId: number | string,
  { id, version }: Pick<Announcement, 'id' | 'version'>,
) => `${userId}:${id}@${version}`
